import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import { type SupportedLocale } from './localized-string';

export interface ConsentTextVersion {
  id: string;
  consent_type_id: string;
  organisation_id: string | null;
  version: number;
  locale: string;
  body: string;
  effective_from: string;
}

export interface ConsentType {
  id: string;
  product_id: string;
  organisation_id: string | null;
  code: string;
  display_name: string;
  display_name_translations?: Partial<Record<SupportedLocale, string>>;
  description: string | null;
  legal_basis: string | null;
  blocking: boolean;
  active: boolean;
  text_versions?: ConsentTextVersion[];
}

export interface ConsentSetting {
  id: string;
  organisation_id: string;
  product_id: string;
  consent_type_id: string;
  enabled: boolean;
  blocking: boolean | null;
}

export interface CreateConsentTypeInput {
  product_id: string;
  organisation_id?: string;
  code: string;
  display_name: string;
  display_name_translations?: Partial<Record<SupportedLocale, string>>;
  description?: string;
  legal_basis?: string;
  blocking?: boolean;
  text?: string;
  text_translations?: Partial<Record<SupportedLocale, string>>;
  locale?: string;
}

export interface UpsertSettingInput {
  organisation_id: string;
  product_id: string;
  consent_type_id: string;
  enabled?: boolean;
  blocking?: boolean | null;
}

const KEYS = {
  types: (productId: string, orgId?: string) =>
    ['consent-types', productId, orgId ?? 'defaults'] as const,
  settings: (orgId: string, productId: string) => ['consent-settings', orgId, productId] as const,
};

export function useConsentTypes(productId: string | undefined, orgId?: string) {
  return useQuery({
    queryKey: productId ? KEYS.types(productId, orgId) : ['consent-types', 'idle'],
    queryFn: () => {
      const params = new URLSearchParams({ product_id: productId! });
      if (orgId) params.set('organisation_id', orgId);
      return apiGet<ConsentType[]>(`/api/consent/types?${params.toString()}`);
    },
    enabled: !!productId,
  });
}

export function useCreateConsentType(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConsentTypeInput) =>
      apiPost<ConsentType>('/api/consent/types', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-types', productId] }),
  });
}

export function useUpdateConsentType(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      display_name?: string;
      display_name_translations?: Partial<Record<SupportedLocale, string>>;
      description?: string;
      legal_basis?: string;
      blocking?: boolean;
    }) => apiPatch<ConsentType>(`/api/consent/types/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-types', productId] }),
  });
}

export function useDeleteConsentType(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/consent/types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-types', productId] }),
  });
}

export function useAddTextVersion(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      body: string;
      version?: number;
      effective_from?: string;
      locale?: string;
      organisation_id?: string;
    }) => apiPost<ConsentTextVersion>(`/api/consent/types/${id}/text-versions`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-types', productId] }),
  });
}

export function useConsentSettings(orgId: string | undefined, productId: string | undefined) {
  return useQuery({
    queryKey: orgId && productId ? KEYS.settings(orgId, productId) : ['consent-settings', 'idle'],
    queryFn: () =>
      apiGet<ConsentSetting[]>(
        `/api/consent/settings?organisation_id=${orgId}&product_id=${productId}`,
      ),
    enabled: !!orgId && !!productId,
  });
}

export function useUpsertSetting(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertSettingInput) =>
      apiPost<ConsentSetting>('/api/consent/settings', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent-settings', orgId, productId] }),
  });
}

export interface CaseConsentAnswer {
  status: string;
  captured_at: string;
  recorded_by: string | null;
  text_version: { id: string; version: number; locale: string; body: string };
}

export interface CaseConsentEntry {
  consent_type_id: string;
  code: string;
  display_name: string;
  description: string | null;
  legal_basis: string | null;
  blocking: boolean;
  configured: boolean;
  wording: string | null;
  answer: CaseConsentAnswer | null;
}

export interface CaseConsentsResponse {
  org_id: string;
  product_id: string;
  case_id: string;
  locale: string;
  consents: CaseConsentEntry[];
}

export function useCaseConsents(
  caseId: string | undefined,
  orgId: string | undefined,
  productId: string | undefined,
  lang?: string,
) {
  return useQuery({
    queryKey: ['case-consents', caseId ?? 'idle', lang ?? 'en'],
    queryFn: () => {
      const params = new URLSearchParams({
        org_id: orgId!,
        product_id: productId!,
        case_id: caseId!,
      });
      if (lang) params.set('lang', lang);
      return apiGet<CaseConsentsResponse>(`/api/consent/case-consents?${params.toString()}`);
    },
    enabled: !!caseId && !!orgId && !!productId,
  });
}
